import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, getErrorMessage } from '../../lib/api';
import type { ProductEndpoint } from '../../types';
import { FlaskConical, CheckCircle2, XCircle, X, Plus } from 'lucide-react';

interface EndpointTesterProps {
  productId: string;
  endpoints: ProductEndpoint[];
  selectedEndpoint: ProductEndpoint | null;
  onEndpointSelect: (endpoint: ProductEndpoint | null) => void;
}

interface TestResult {
  success: boolean;
  status_code?: number;
  response_time_ms?: number;
  execution_time_ms?: number;
  request?: {
    method: string;
    url: string;
    headers: Record<string, string>;
    body?: any;
  };
  response?: {
    status?: number;
    statusText?: string;
    headers: Record<string, string>;
    body?: any;
    data?: any;
  };
  error?: string;
  errors?: Array<{ type?: string; message: string }>;
  transformation_applied?: boolean;
}

export const EndpointTester: React.FC<EndpointTesterProps> = ({
  endpoints,
  selectedEndpoint,
  onEndpointSelect,
}) => {
  const [environment, setEnvironment] = useState<'sandbox' | 'production'>('sandbox');
  const [requestParams, setRequestParams] = useState<Array<{ key: string; value: string }>>([
    { key: '', value: '' },
  ]);
  const [requestHeaders, setRequestHeaders] = useState<Array<{ key: string; value: string }>>([
    { key: '', value: '' },
  ]);
  const [requestBody, setRequestBody] = useState('{\n  \n}');
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  // Auto-populate path parameters (e.g. {notificationToken}) when selectedEndpoint changes
  useEffect(() => {
    if (!selectedEndpoint) return;
    const pathStr = selectedEndpoint.external_path || selectedEndpoint.public_path || '';
    const matches = Array.from(pathStr.matchAll(/\{([^}]+)\}/g)).map((m) => m[1]);
    if (matches.length > 0) {
      setRequestParams(matches.map((key) => ({ key, value: '' })));
    } else {
      setRequestParams([{ key: '', value: '' }]);
    }
  }, [selectedEndpoint]);

  const testMutation = useMutation({
    mutationFn: async () => {
      if (!selectedEndpoint) {
        throw new Error('Nenhum endpoint selecionado');
      }

      const params: Record<string, string> = {};
      requestParams.forEach((p) => {
        if (p.key && p.value) {
          params[p.key] = p.value;
        }
      });

      const headers: Record<string, string> = {};
      requestHeaders.forEach((h) => {
        if (h.key && h.value) {
          headers[h.key] = h.value;
        }
      });

      let body = undefined;
      if (['POST', 'PUT', 'PATCH'].includes(selectedEndpoint.public_method)) {
        try {
          body = JSON.parse(requestBody);
        } catch {
          body = requestBody;
        }
      }

      const response = await api.post(`/portal/admin/endpoints/${selectedEndpoint.id}/test`, {
        environment,
        parameters: params,
        params,
        headers,
        body,
      });
      return response.data?.data || response.data;
    },
    onSuccess: (data: TestResult) => {
      const firstErr =
        data.error ||
        (Array.isArray(data.errors) && data.errors.length > 0
          ? data.errors.map((e) => e.message).join(' | ')
          : undefined);
      setTestResult({
        ...data,
        status_code: data.status_code ?? data.response?.status,
        response_time_ms: data.response_time_ms ?? data.execution_time_ms,
        error: !data.success ? firstErr : undefined,
      });
    },
    onError: (error) => {
      setTestResult({
        success: false,
        error: getErrorMessage(error),
      });
    },
  });

  const addRequestParam = () => {
    setRequestParams([...requestParams, { key: '', value: '' }]);
  };

  const removeRequestParam = (index: number) => {
    setRequestParams(requestParams.filter((_, i) => i !== index));
  };

  const updateRequestParam = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...requestParams];
    updated[index][field] = value;
    setRequestParams(updated);
  };

  const addRequestHeader = () => {
    setRequestHeaders([...requestHeaders, { key: '', value: '' }]);
  };

  const removeRequestHeader = (index: number) => {
    setRequestHeaders(requestHeaders.filter((_, i) => i !== index));
  };

  const updateRequestHeader = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...requestHeaders];
    updated[index][field] = value;
    setRequestHeaders(updated);
  };

  const resolvedResponseBody =
    testResult?.response?.body !== undefined
      ? testResult.response.body
      : testResult?.response?.data;

  return (
    <div className="space-y-6">
      {/* Endpoint Selector */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Selecionar Endpoint</label>
            <select
              className="select"
              value={selectedEndpoint?.id || ''}
              onChange={(e) => {
                const endpoint = endpoints.find((ep) => ep.id === e.target.value);
                onEndpointSelect(endpoint || null);
                setTestResult(null);
              }}
            >
              <option value="">-- Selecione um Endpoint --</option>
              {endpoints.map((endpoint) => (
                <option key={endpoint.id} value={endpoint.id}>
                  {endpoint.public_method} {endpoint.public_path} — {endpoint.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Ambiente de Destino</label>
            <select
              className="select"
              value={environment}
              onChange={(e) => setEnvironment(e.target.value as 'sandbox' | 'production')}
            >
              <option value="sandbox">Sandbox (Homologação)</option>
              <option value="production">Produção</option>
            </select>
          </div>
        </div>
      </div>

      {!selectedEndpoint ? (
        <div className="card text-center py-12">
          <p className="text-slate-600 dark:text-slate-300 mb-4">
            Selecione um endpoint acima para iniciar o teste direto à API de destino.
          </p>
          {endpoints.length === 0 && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Nenhum endpoint disponível. Crie ou importe os endpoints no separador Endpoints ou
              OpenAPI Spec.
            </p>
          )}
        </div>
      ) : (
        <>
          {/* Test Request Form */}
          <div className="card">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
              Configurar Requisição de Teste
            </h3>

            {/* Path & Query Parameters */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <label className="label mb-0">
                  Parâmetros de Rota (`{'{param}'}`) e Query String
                </label>
                <button
                  onClick={addRequestParam}
                  className="btn-secondary text-sm inline-flex items-center gap-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Adicionar Parâmetro</span>
                </button>
              </div>
              <div className="space-y-2">
                {requestParams.map((param, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <input
                      type="text"
                      placeholder="Nome do parâmetro (ex: notificationToken)"
                      className="input flex-1"
                      value={param.key}
                      onChange={(e) => updateRequestParam(index, 'key', e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Valor"
                      className="input flex-1"
                      value={param.value}
                      onChange={(e) => updateRequestParam(index, 'value', e.target.value)}
                    />
                    <button
                      onClick={() => removeRequestParam(index)}
                      className="btn-danger text-sm p-2"
                      disabled={requestParams.length === 1}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Headers */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <label className="label mb-0">Cabeçalhos HTTP (Headers)</label>
                <button
                  onClick={addRequestHeader}
                  className="btn-secondary text-sm inline-flex items-center gap-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Adicionar Header</span>
                </button>
              </div>
              <div className="space-y-2">
                {requestHeaders.map((header, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <input
                      type="text"
                      placeholder="Nome do Header (ex: Authorization)"
                      className="input flex-1"
                      value={header.key}
                      onChange={(e) => updateRequestHeader(index, 'key', e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Valor do Header"
                      className="input flex-1"
                      value={header.value}
                      onChange={(e) => updateRequestHeader(index, 'value', e.target.value)}
                    />
                    <button
                      onClick={() => removeRequestHeader(index)}
                      className="btn-danger text-sm p-2"
                      disabled={requestHeaders.length === 1}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Request Body */}
            {['POST', 'PUT', 'PATCH'].includes(selectedEndpoint.public_method) && (
              <div className="mb-6">
                <label className="label">Corpo da Requisição (JSON)</label>
                <textarea
                  className="textarea h-48 font-mono text-xs"
                  value={requestBody}
                  onChange={(e) => setRequestBody(e.target.value)}
                />
              </div>
            )}

            {/* Test Button */}
            <button
              onClick={() => testMutation.mutate()}
              disabled={testMutation.isPending}
              className="btn-primary w-full inline-flex items-center justify-center gap-2"
            >
              <FlaskConical className="w-4 h-4" />
              <span>{testMutation.isPending ? 'A executar teste...' : 'Executar Teste'}</span>
            </button>
          </div>

          {/* Test Results */}
          {testResult && (
            <div className="card">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                Resultado do Teste
              </h3>

              {/* Status Summary */}
              <div className="mb-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {testResult.success ? (
                      <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                    ) : (
                      <XCircle className="w-6 h-6 text-rose-500" />
                    )}
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-white">
                        {testResult.success ? 'Teste concluído com sucesso' : 'Falha no teste'}
                      </p>
                      {testResult.status_code !== undefined && testResult.status_code > 0 && (
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          Código HTTP: {testResult.status_code}
                        </p>
                      )}
                    </div>
                  </div>
                  {testResult.response_time_ms !== undefined && (
                    <div className="text-right">
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        Tempo de Resposta
                      </p>
                      <p className="text-lg font-semibold text-slate-900 dark:text-white">
                        {testResult.response_time_ms}ms
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Error Message */}
              {testResult.error && (
                <div className="mb-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 p-4 border border-rose-200 dark:border-rose-900/50">
                  <p className="text-sm font-medium text-rose-800 dark:text-rose-300">
                    Detalhe do Erro
                  </p>
                  <p className="text-sm text-rose-700 dark:text-rose-400 mt-1">
                    {testResult.error}
                  </p>
                </div>
              )}

              {/* Request Details */}
              {testResult.request && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
                    Requisição Enviada
                  </h4>
                  <div className="bg-slate-50 dark:bg-slate-800/60 rounded-xl p-3 border border-slate-200 dark:border-slate-700">
                    <p className="text-xs text-slate-600 dark:text-slate-300 mb-2">
                      <span className="font-mono font-semibold">{testResult.request.method}</span>{' '}
                      <span className="font-mono">{testResult.request.url}</span>
                    </p>
                    {Object.keys(testResult.request.headers || {}).length > 0 && (
                      <details className="mb-2">
                        <summary className="text-xs text-primary-600 dark:text-primary-400 cursor-pointer">
                          Headers ({Object.keys(testResult.request.headers).length})
                        </summary>
                        <pre className="mt-1 text-xs bg-white dark:bg-slate-900 p-2 rounded border border-slate-200 dark:border-slate-700 overflow-auto">
                          {JSON.stringify(testResult.request.headers, null, 2)}
                        </pre>
                      </details>
                    )}
                    {testResult.request.body && (
                      <details>
                        <summary className="text-xs text-primary-600 dark:text-primary-400 cursor-pointer">
                          Corpo (Body)
                        </summary>
                        <pre className="mt-1 text-xs bg-white dark:bg-slate-900 p-2 rounded border border-slate-200 dark:border-slate-700 overflow-auto max-h-48">
                          {JSON.stringify(testResult.request.body, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              )}

              {/* Response Details */}
              {testResult.response && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-slate-900 dark:text-white">
                      Resposta Recebida
                    </h4>
                    {testResult.transformation_applied && (
                      <span className="badge badge-info text-xs">Transformação Aplicada</span>
                    )}
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-800/60 rounded-xl p-3 border border-slate-200 dark:border-slate-700">
                    {testResult.response.headers &&
                      Object.keys(testResult.response.headers).length > 0 && (
                        <details className="mb-2">
                          <summary className="text-xs text-primary-600 dark:text-primary-400 cursor-pointer">
                            Headers ({Object.keys(testResult.response.headers).length})
                          </summary>
                          <pre className="mt-1 text-xs bg-white dark:bg-slate-900 p-2 rounded border border-slate-200 dark:border-slate-700 overflow-auto">
                            {JSON.stringify(testResult.response.headers, null, 2)}
                          </pre>
                        </details>
                      )}
                    <details open>
                      <summary className="text-xs text-primary-600 dark:text-primary-400 cursor-pointer">
                        Corpo da Resposta (Body)
                      </summary>
                      <pre className="mt-1 text-xs bg-white dark:bg-slate-900 p-2 rounded border border-slate-200 dark:border-slate-700 overflow-auto max-h-96">
                        {typeof resolvedResponseBody === 'string'
                          ? resolvedResponseBody
                          : JSON.stringify(resolvedResponseBody, null, 2)}
                      </pre>
                    </details>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
